import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { Button, Card, StatusBadge } from '@/components/UI';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { AppRouter } from '@/types/appRouter';
import { formatTimestamp } from '../entryOptimizerUtils';

type EntryOptimizerRecommendation = inferRouterOutputs<AppRouter>['entryOptimizer']['listRecommendations']['recommendations'][number];

/**
 * External-agent recommendations for this sweep with their approval state. The
 * approve/reject decision lives here; an approved promote is then executed by the
 * agent (creates an inactive family variant after real-entry confirmation) or interactively via the candidates table.
 */
const WorkbenchRecommendationsCard = ({ jobId }: { jobId: number }) => {
    const queryClient = useQueryClient();

    const recommendationsQuery = useAppQuery({
        ...trpc.entryOptimizer.listRecommendations.queryOptions({ jobId })
    });

    const decideApprovalMutation = useMutation({
        ...trpc.entryOptimizer.decideApproval.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (result) => {
            showToast(`Approval ${result.approval.status}`, 'success');
            await queryClient.invalidateQueries(trpc.entryOptimizer.listRecommendations.pathFilter());
        }
    });

    const recommendations = recommendationsQuery.data?.recommendations ?? [];

    if (recommendationsQuery.isLoading || recommendations.length === 0) {
        return null;
    }

    return (
        <Card
            className="mt-4"
            subtitle="External AI verdicts for this sweep. Approval lets the agent create an inactive family variant after real-entry confirmation."
            title="Workbench Recommendations"
        >
            <div className="flex flex-col gap-2">
                {recommendations.map((recommendation) => (
                    <RecommendationRow
                        isDeciding={decideApprovalMutation.isPending}
                        key={recommendation.artifactId}
                        recommendation={recommendation}
                        onDecide={(approvalId, status) => decideApprovalMutation.mutate({ approvalId, status })}
                    />
                ))}
            </div>
        </Card>
    );
};

const RecommendationRow = ({
    isDeciding,
    recommendation,
    onDecide
}: {
    isDeciding: boolean;
    recommendation: EntryOptimizerRecommendation;
    onDecide: (approvalId: number, status: 'approved' | 'rejected') => void;
}) => {
    const { approval } = recommendation;
    const isKeepIncumbent = recommendation.candidateId === null;

    return (
        <div className="flex flex-col gap-2 rounded-md border border-zinc-800 bg-black/20 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-zinc-100">{recommendation.name}</div>
                    <div className="mt-0.5 text-xs text-zinc-500">
                        {recommendation.externalTool} · artifact #{recommendation.artifactId} · {formatTimestamp(recommendation.createdAt)}
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge status={recommendation.artifactStatus}>{recommendation.artifactStatus}</StatusBadge>
                    {approval && <StatusBadge status={approval.status}>approval {approval.status}</StatusBadge>}
                </div>
            </div>

            {recommendation.summary && <p className="text-sm text-zinc-300">{recommendation.summary}</p>}
            {recommendation.rationale && <p className="text-xs text-zinc-500">{recommendation.rationale}</p>}

            {!isKeepIncumbent && approval?.status === 'requested' && (
                <div className="flex items-center justify-between gap-2 border-t border-zinc-800 pt-2">
                    <span className="text-xs text-zinc-500">
                        Requested by {approval.requestedBy}
                        {approval.rationale ? ` — ${approval.rationale}` : ''}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            disabled={isDeciding}
                            size="sm"
                            variant="destructive"
                            onClick={() => onDecide(approval.approvalId, 'rejected')}
                        >
                            Reject
                        </Button>
                        <Button disabled={isDeciding} size="sm" onClick={() => onDecide(approval.approvalId, 'approved')}>
                            Approve
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkbenchRecommendationsCard;
